import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  CacheLookupPolicy,
  ClientAuthError,
  ClientAuthErrorCodes,
  InteractionRequiredAuthError,
  PublicClientApplication,
  ServerError,
  type AccountInfo,
} from "@azure/msal-browser";

/**
 * Two MSAL instances mirror the portal's two trust paths:
 * - Internal: iQuest staff against the single-tenant iQuest Entra app.
 * - External: client users against the multi-tenant Entra app.
 *
 * The sign-in page always starts the external instance. Its `organizations`
 * authority accepts the iQuest tenant as well, and the backend assigns roles
 * from the portal_users row. The internal instance has no sign-in button; it
 * stays so a session already cached against the single-tenant app still
 * restores.
 */

const internalTenant = import.meta.env.VITE_AZURE_INTERNAL_TENANT_ID;
const internalClient = import.meta.env.VITE_AZURE_INTERNAL_SPA_CLIENT_ID;
const internalScope = import.meta.env.VITE_AZURE_INTERNAL_API_SCOPE;

const externalClient = import.meta.env.VITE_AZURE_EXTERNAL_SPA_CLIENT_ID;
const externalScope = import.meta.env.VITE_AZURE_EXTERNAL_API_SCOPE;

export type TrustPath = "internal" | "external";

// localStorage is shared across tabs of this origin. Since msal-browser 4.26.0,
// durable entries are stored in the clear, and so survive a browser restart,
// only when the id token's `signin_state` contains `kmsi` (Yes on Microsoft's
// "Stay signed in?" prompt) or `dvc_dmjd` (a domain-joined device). Every other
// entry is AES-GCM encrypted with a key in the session cookie
// `msal.cache.encryption`, which the browser drops when the browser process
// exits. The next initialize() cannot decrypt that ciphertext and deletes it.
// There is no supported config flag to disable the encryption.
// A cache written by v3 is left in place and re-imported for up to
// cacheRetentionDays (default 5) after that user's last v3 write.
// `storeAuthStateInCookie` was removed from CacheOptions in v5.
const cache = { cacheLocation: "localStorage" as const };

// Interactive sign-in, sign-out, and acquireTokenRedirect return to the origin
// root, where initAuth runs handleRedirectPromise. Only silent renewal uses the
// redirect bridge (#315), passed per request in getAccessToken, so a bridge
// that breaks or is missing from an Entra app falls back to a sign-in redirect
// instead of breaking sign-in. Both URIs are SPA redirect URIs on both apps.
const SILENT_REDIRECT_URI = `${window.location.origin}/redirect.html`;

const instances: Record<TrustPath, PublicClientApplication> = {
  internal: new PublicClientApplication({
    auth: {
      clientId: internalClient,
      authority: `https://login.microsoftonline.com/${internalTenant}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache,
  }),
  external: new PublicClientApplication({
    auth: {
      clientId: externalClient,
      authority: "https://login.microsoftonline.com/organizations",
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache,
  }),
};

const scopes: Record<TrustPath, string> = {
  internal: internalScope,
  external: externalScope,
};

let activePath: TrustPath | null = null;
let activeAccount: AccountInfo | null = null;

export async function initAuth(): Promise<AccountInfo | null> {
  for (const path of ["external", "internal"] as TrustPath[]) {
    const msal = instances[path];
    await msal.initialize();
    const result = await msal.handleRedirectPromise();
    if (result?.account) {
      activePath = path;
      activeAccount = result.account;
      msal.setActiveAccount(result.account);
      return activeAccount;
    }
    const accounts = msal.getAllAccounts();
    if (accounts.length > 0) {
      activePath = path;
      activeAccount = accounts[0];
      msal.setActiveAccount(accounts[0]);
      return activeAccount;
    }
  }
  return null;
}

export async function signIn(path: TrustPath = "external"): Promise<void> {
  const msal = instances[path];
  await msal.initialize();
  return msal.loginRedirect({
    scopes: [scopes[path]],
    prompt: "select_account",
  });
}

export async function signOut(): Promise<void> {
  if (!activePath) return;
  return instances[activePath].logoutRedirect();
}

export function currentAccount(): AccountInfo | null {
  return activeAccount;
}

/**
 * Silent renewal could not reach Microsoft. Callers must not navigate away:
 * a redirect while the machine is offline lands on the browser's offline page.
 */
export class PortalNetworkError extends Error {
  readonly code = "network" as const;

  constructor(
    message = "Could not refresh your sign-in because the network is unavailable.",
  ) {
    super(message);
    this.name = "PortalNetworkError";
  }
}

const CLIENT_NETWORK_CODES = new Set<string>([
  ClientAuthErrorCodes.endpointResolutionError,
  ClientAuthErrorCodes.networkError,
  ClientAuthErrorCodes.openIdConfigError,
]);

const BROWSER_NETWORK_CODES = new Set<string>([
  BrowserAuthErrorCodes.noNetworkConnectivity,
  BrowserAuthErrorCodes.postRequestFailed,
  BrowserAuthErrorCodes.getRequestFailed,
]);

function isNetworkError(error: unknown): boolean {
  // Name check, not instanceof. NetworkError is not re-exported by
  // @azure/msal-browser, and @azure/msal-common is not a direct dependency.
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { name?: string }).name === "NetworkError"
  );
}

function isPortalNetworkFailure(error: unknown): boolean {
  if (isNetworkError(error)) return true;
  if (error instanceof ClientAuthError && CLIENT_NETWORK_CODES.has(error.errorCode)) {
    return true;
  }
  if (error instanceof BrowserAuthError && BROWSER_NETWORK_CODES.has(error.errorCode)) {
    return true;
  }
  return false;
}

/**
 * Hidden-iframe renewal got no answer from redirect.html within 10s while
 * online, for example when Microsoft refused SILENT_REDIRECT_URI. Sign in again.
 */
function isIframeSignInTimeout(error: unknown): boolean {
  return (
    navigator.onLine !== false &&
    error instanceof BrowserAuthError &&
    error.errorCode === BrowserAuthErrorCodes.timedOut
  );
}

/**
 * Neither the refresh token nor hidden-iframe renewal produced a token, so the
 * user has to go to Microsoft: anything interaction_required (login_required
 * from the iframe when the Microsoft session has ended, or third-party cookies
 * are blocked), an invalid_grant that msal-common raises as a plain
 * ServerError, or the iframe timing out while online.
 */
function needsSignIn(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) return true;
  if (error instanceof ServerError && error.errorCode === "invalid_grant") return true;
  return isIframeSignInTimeout(error);
}

export async function getAccessToken(): Promise<string> {
  if (!activePath || !activeAccount) throw new Error("Not signed in");
  const msal = instances[activePath];
  const request = {
    scopes: [scopes[activePath]],
    account: activeAccount,
  };

  // A still-valid access token can be read from cache with no network. Anything
  // else (expired token, cache miss) must fail here instead of opening a
  // refresh request that cannot complete while the browser is offline.
  if (navigator.onLine === false) {
    try {
      const cached = await msal.acquireTokenSilent({
        ...request,
        cacheLookupPolicy: CacheLookupPolicy.AccessToken,
      });
      return cached.accessToken;
    } catch {
      throw new PortalNetworkError();
    }
  }

  try {
    // Default: cached access token, then the refresh token, then, once the
    // 24h SPA refresh token has lapsed, a hidden iframe on the Microsoft
    // session that lands on SILENT_REDIRECT_URI. That last step is what keeps
    // a returning user on the page instead of a full-page redirect (#315).
    const r = await msal.acquireTokenSilent({
      ...request,
      cacheLookupPolicy: CacheLookupPolicy.Default,
      redirectUri: SILENT_REDIRECT_URI,
    });
    return r.accessToken;
  } catch (error) {
    // By the time one of these surfaces, msal has tried the iframe or ruled it
    // out, so a redirect is the only way left to a token. A real network
    // failure on the refresh call is a NetworkError.
    if (needsSignIn(error)) {
      await msal.acquireTokenRedirect({ scopes: [scopes[activePath]] });
      throw new Error("Redirecting for token");
    }
    // Network-class failures become PortalNetworkError so the shell can show
    // a notice without leaving the page.
    if (isPortalNetworkFailure(error)) throw new PortalNetworkError();
    // Anything else is rethrown as-is. An unexpected MSAL error must not
    // navigate: a redirect can land on the browser offline page, or interrupt
    // a session that is still recoverable.
    throw error;
  }
}
